import { Injectable } from "@angular/core";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import {
    DisplayHandler,
    IAggregatedValues,
    IColumnTemplate,
    IGridDisplayViewColumn, IGridViewColumn
} from "@main/reporting.v3/widget.grid/widget-grid.models";
import { Replacer } from "@main/reporting.v3/core/regex/regex-utility.models";
import { DataType, InputType, IQuerySchemaColumn, IViewMetadataColumn } from "@main/reporting.v3/core/reporting-models";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DisplayMode, GridHelper, IGridColumn } from "@features/grid";
import * as moment from "moment/moment";


@Injectable()
export class WidgetGridAuxiliaryService {
    public readonly gridTableClass: string = "widget-grid-table";
    public readonly widgetContainerClass: string = "widget-grid-container";

    constructor(private readonly htmlRenderingService: HtmlRenderingService,
                private readonly sanitizer: DomSanitizer) {
    }

    public getDisplayHandler(column: IGridDisplayViewColumn, gridColumn: IGridColumn): DisplayHandler {
        return cellValue => {
            const value: string = GridHelper.convertCellRawValueToString(cellValue, gridColumn);

            // TODO: This patient-id handling bypasses the column mask and displays the unhashed id.
            // Detection also depends on the query alias ending in patientId; a user-defined alias
            // without that suffix prevents the column from being recognised as a patient id.
            if (column.queryAlias.toLowerCase().endsWith("patientid") &&
                typeof value === "string") {
                return value.split("-")[0];
            }

            const mask: string = column.extraSettings.mask;
            if (!mask?.length) return value;

            let index: number = 0;
            return column.extraSettings.mask.replace(/_/g, (substring: string): string => {
                const prefix: string = substring.slice(0, -1);
                if (index >= value.length) return prefix;
                return prefix + value[index++];
            });
        }
    }

    public getCustomFormattedCellHtml(columns: IGridDisplayViewColumn[], template: IColumnTemplate, rowData: any): SafeHtml {
        if (!columns?.length || !template?.customTemplate?.length) return "";

        // Replace all occurrences of column query aliases by the value corresponding to that column in this row.
        const replacer: Replacer = (_: string, fieldName: string): string => {
            if (fieldName !== "col") {
                const column: IGridDisplayViewColumn = columns.find(col =>
                    col.queryAlias.toLowerCase() === fieldName.toLowerCase());

                return !!column && !!rowData ? rowData[column.queryAlias.replace(".", "_")] ?? "" : "";
            }
            return !!template.column ? GridHelper.getCellDisplayValue(rowData, template.column) : "";
        }

        const output: string = template.customTemplate.replace(/\{(?<fieldName>.*?)}/gm, replacer);

        return this.sanitizer.bypassSecurityTrustHtml(this.htmlRenderingService.sanitizeHtml(output));
    }

    public fixCustomFormatReferences(buildColumns: IGridViewColumn[], viewColumns: IGridViewColumn[]): void {
        buildColumns.forEach(buildColumn => {
            if (!buildColumn?.extraSettings?.customFormat) return;

            const pattern: RegExp = /\{(?<fieldName>[^}]+?)}/gm;

            buildColumn.extraSettings.customFormat = buildColumn.extraSettings.customFormat
                .replace(pattern, (match: string, fieldName: string): string => {

                    // If field name is not captured or this query alias is not missing, avoid making changes to this match.
                    if (!fieldName || buildColumns.some(column => column.queryAlias === fieldName)) return match;

                    const viewColumn: IGridViewColumn = viewColumns
                        .find(column => column.queryAlias === fieldName);

                    const similarColumn: IGridViewColumn = buildColumns
                        .find(column => viewColumn?.columnName?.localeCompare(column.columnName) === 0);

                    if (!viewColumn || !similarColumn) return match;

                    return `{${similarColumn.queryAlias}}`;
                });
        });
    }

    public createMissingColumns(buildColumns: IGridViewColumn[], viewColumns: IGridViewColumn[],
                                schemaColumns: IQuerySchemaColumn[], metadataColumns: IViewMetadataColumn[]): void {
        // Get missing schema columns.
        // (query schema columns whose query alias do not belong to any grid column).
        const missingColumns: IQuerySchemaColumn[] = schemaColumns
            .filter(schemaColumn => !buildColumns
                .some(column => column.queryAlias === schemaColumn.queryAlias));

        // Convert missing columns to grid view columns, and add them to grid view.
        buildColumns.push(...missingColumns.map(schemaColumn => {

            const metadataColumn: IViewMetadataColumn = metadataColumns
                .find(metadataColumn => metadataColumn.queryAlias === schemaColumn.queryAlias);

            const columnName: string = schemaColumn.baseColumnName ?? schemaColumn.columnName;

            const similarColumn: IGridViewColumn = viewColumns
                .find(column => columnName?.localeCompare(column.columnName) === 0);

            return this.createGridViewColumn(schemaColumn, similarColumn, metadataColumn);
        }));
    }

    public createGridViewColumn(schemaColumn: IQuerySchemaColumn, similarColumn?: IGridViewColumn,
                                metadataColumn?: IViewMetadataColumn): IGridViewColumn {

        const header: string = similarColumn?.header ?? metadataColumn?.title ?? schemaColumn.queryAlias;
        const visible: boolean = similarColumn?.visible ?? true;
        const sortable: boolean = similarColumn?.sortable ?? true;
        const dataType: DataType = schemaColumn.dataType ?? "string";

        const displayMode: DisplayMode = !!similarColumn?.displayMode && similarColumn.dataType === dataType
            ? similarColumn.displayMode
            : this.getDisplayModeByDataType(dataType);

        const inputType: InputType = !!similarColumn?.inputType && similarColumn.dataType === dataType
            ? similarColumn.inputType
            : this.getInputTypeByDataType(dataType);

        const footer: any = !!similarColumn?.footer && similarColumn.dataType === dataType
            ? similarColumn.footer : {};

        const extraSettings: any = !!similarColumn?.extraSettings && similarColumn.dataType === dataType
            ? similarColumn.extraSettings : {};

        return {
            // Set query alias and column name from query schema (cannot reuse the ones from similar column).
            queryAlias: schemaColumn.queryAlias,
            columnName: schemaColumn.baseColumnName ?? schemaColumn.columnName,
            // Set the remaining ones from similar column if exists, or from column metadata if not.
            header: header,
            dataType: dataType,
            footer: footer,
            visible: visible,
            sortable: sortable,
            inputType: inputType,
            displayMode: displayMode,
            extraSettings: extraSettings
        } as IGridViewColumn;
    }

    public createFooterOutput(columns: IGridDisplayViewColumn[], values: any): IAggregatedValues {
        if (!values) return null;

        const result: IAggregatedValues = {};

        Object.keys(values).forEach(key => {
            const index: number = columns.findIndex(column => key === column.queryAlias);
            const column: IGridDisplayViewColumn = columns[index];

            if (index === -1 || !column || !column.footer) {
                return;
            }

            if (index > 0 && column.footer.leftCellLabel) {
                const previousColumn: IGridDisplayViewColumn = columns[index - 1];
                result[previousColumn.queryAlias] = {
                    value: column.footer.leftCellLabel,
                    textAlign: column.footer.leftCellLabelAlignment
                };
            }

            result[key] = {
                textAlign: column.footer.textAlignment || "left",
                value: column.footer.outputFormat || values[key]
                    .map((_: string, index: number) => `{${index}}`).join(" / ")
            };

            values[key].forEach((value: string, index: number) =>
                result[key].value = String(result[key].value).replace(`{${index}}`,
                    moment.isDate(value) ? moment(value).format("L") : value));
        });

        return this.replaceDotsInFieldNames([result])[0];
    }

    public getDisplayModeByDataType(dataType: DataType): DisplayMode {
        switch (dataType) {
            case "bool":
                return DisplayMode.Conditional;
            case "date":
                return DisplayMode.Date;
            case "numeric":
                return DisplayMode.Number;
            default:
                return DisplayMode.Text;
        }
    }

    public getInputTypeByDataType(dataType: DataType): InputType {
        switch (dataType) {
            case "bool":
                return "checkbox";
            case "date":
                return "calendar";
            case "numeric":
                return "number";
            default:
                return "text";
        }
    }

    public getVariableType(column: IGridDisplayViewColumn): string {
        switch (column.dataType) {
            case "numeric":
                return "number";
            case "other":
                return "string";
            default:
                return column.dataType;
        }
    }

    public replaceDotsInFieldNames(rows: any[]): any[] {
        return rows.map(row => {
            const result = <any>{};
            Object.keys(row).forEach(key => result[key.replace(".", "_")] = row[key]);
            return result;
        });
    }
}